import posthog from "posthog-js";
import logger from "./logger";
import { APP_VERSION } from "./version";

let initialized = false;

const isLocalhost = () => {
  if (typeof window === "undefined") return false;
  const hostname = window.location.hostname;
  return hostname === "localhost" || hostname === "127.0.0.1";
};

/**
 * Initialize PostHog tracker.
 * Call once on app startup in App.jsx.
 */
export const initPostHog = (options = {}) => {
  if (initialized) return;

  const apiKey = process.env.REACT_APP_POSTHOG_KEY;
  const apiHost = process.env.REACT_APP_POSTHOG_HOST || "https://us.i.posthog.com";

  // Without a real project key PostHog only logs failed requests, so skip it
  if (!apiKey || apiKey.startsWith("phc_demo_key")) {
    logger.log("[PostHog] Skipped: set REACT_APP_POSTHOG_KEY to enable analytics");
    return;
  }

  const config = {
    api_host: apiHost,
    autocapture: true,
    capture_pageview: true,
    disable_session_recording: false,
    loaded: ph => {
      ph.register({
        environment: process.env.NODE_ENV || "development",
        api_url: process.env.REACT_APP_API_URL || "https://api.sandbox.spacia.page",
        app_version: APP_VERSION,
      });
      if (process.env.NODE_ENV !== "production") {
        logger.log("[PostHog] Initialized successfully with host:", apiHost);
      }
    },
    ...options,
  };

  if (isLocalhost() && !options.captureOnLocalhost) {
    // Disable capturing by default on localhost unless explicitly enabled
    config.opt_out_capturing_by_default = true;
    logger.log("[PostHog] Tracking opted out on localhost (pass captureOnLocalhost: true to enable)");
  }

  posthog.init(apiKey, config);
  initialized = true;
};

const isAdminUser = role => {
  if (!role) return false;
  const normalized = String(role).toUpperCase();
  return (
    normalized === "ROLE_ADMINISTRATOR" ||
    normalized === "ADMINISTRATOR" ||
    normalized === "ADMIN" ||
    normalized === "ROLE_ADMIN" ||
    normalized === "ROLE_SUPER_ADMIN" ||
    normalized.includes("ADMIN")
  );
};

/**
 * Identify a user in PostHog upon login/rehydration to enable user-targeted Feature Flags & A/B Testing.
 * Automatically excludes and opts out Platform Administrators to prevent polluting analytics data.
 * @param {Object} user User object containing id, email, role, etc.
 */
export const identifyUser = user => {
  if (!user || !user.id) return;

  const role = user.role || user.userRole;
  if (isAdminUser(role)) {
    // Platform Administrator - completely opt out from tracking and session replays
    posthog.opt_out_capturing();
    if (process.env.NODE_ENV !== "production") {
      logger.log("[PostHog] Platform Administrator detected. Telemetry opted out:", user.id, role);
    }
    return;
  }

  // Ensure capturing is opted in for regular customers, corporate accounts, and hosts
  if (posthog.has_opted_out_capturing()) {
    posthog.opt_in_capturing();
  }

  const userId = String(user.id);
  const userProperties = {
    email: user.email || user.emailAddress || user.username,
    name: user.firstName ? `${user.firstName} ${user.lastName || ""}`.trim() : user.name,
    role: role,
    organizationId: user.organizationId,
    environment: process.env.NODE_ENV,
    app_version: APP_VERSION,
  };

  posthog.identify(userId, userProperties);
  if (process.env.NODE_ENV !== "production") {
    logger.log("[PostHog] User identified:", userId, userProperties);
  }
};

/**
 * Reset PostHog user session state on logout
 */
export const resetUser = () => {
  if (posthog.has_opted_out_capturing()) {
    posthog.opt_in_capturing();
  }
  posthog.reset();
  if (process.env.NODE_ENV !== "production") {
    logger.log("[PostHog] User session reset");
  }
};

/**
 * Capture a custom event in PostHog
 * @param {string} eventName Name of the event
 * @param {Object} properties Custom properties for the event
 */
export const trackPostHogEvent = (eventName, properties = {}) => {
  posthog.capture(eventName, properties);
  if (process.env.NODE_ENV !== "production") {
    logger.log(`[PostHog] Captured event: ${eventName}`, properties);
  }
};

/**
 * Associate the user/session with a B2B Company / Organization (Group Analytics)
 * @param {string|number} companyId Unique ID of the company
 * @param {Object} companyProperties Properties like company name, plan, seat count, country
 */
export const groupCompany = (companyId, companyProperties = {}) => {
  if (!companyId) return;
  posthog.group("company", String(companyId), companyProperties);
  if (process.env.NODE_ENV !== "production") {
    logger.log("[PostHog] Group set (company):", companyId, companyProperties);
  }
};

/**
 * Capture exceptions and runtime errors in PostHog (linked to Session Replays)
 * @param {Error|string} error The error instance or message
 * @param {Object} properties Additional context metadata
 */
export const captureException = (error, properties = {}) => {
  posthog.capture("$exception", {
    $exception_message: error?.message || String(error),
    $exception_type: error?.name || "Error",
    $exception_stack_trace_raw: error?.stack,
    ...properties,
  });
  if (process.env.NODE_ENV !== "production") {
    logger.error("[PostHog] Exception captured:", error, properties);
  }
};

/**
 * Check if a Feature Flag is enabled for the current user
 * @param {string} flagKey Key of the feature flag configured in PostHog
 * @returns {boolean}
 */
export const isFeatureEnabled = flagKey => {
  return posthog.isFeatureEnabled(flagKey) ?? false;
};

/**
 * Get feature flag variant (used for A/B Testing)
 * @param {string} flagKey Key of the experiment flag configured in PostHog
 * @returns {string|boolean|undefined}
 */
export const getFeatureFlag = flagKey => {
  return posthog.getFeatureFlag(flagKey);
};

/**
 * Get JSON payload attached to a feature flag / remote config
 * @param {string} flagKey Key of the feature flag configured in PostHog
 * @returns {any}
 */
export const getFeatureFlagPayload = flagKey => {
  return posthog.getFeatureFlagPayload(flagKey);
};

export default posthog;

